<template>
  <div style="width: 100%; flex: 1 1 auto">
    <ag-grid-vue
      theme="legacy"
      :rowData="drawItemHistory"
      :rowClassRules="rowClassRules"
      :columnDefs="colDefs"
      :defaultColDef="defaultColDefs"
      :autoSizeStrategy="{ type: 'fitCellContents' }"
      :suppressCellFocus="true"
      :singleClickEdit="true"
      :stopEditingWhenCellsLoseFocus="true"
      :suppressDragLeaveHidesColumns="true"
      :getRowHeight="getRowHeight"
      rowSelection="single"
      class="py-3 h-full"
      :class="darkVal ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'"
      @grid-size-changed="resize"
      @grid-ready="(event: GridReadyEvent) => gridApi = event.api"
    ></ag-grid-vue>
  </div>
</template>

<script setup lang="ts">
import { AgGridVue } from "ag-grid-vue3";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { currencyFormatter, percentFormatter } from "@/services/Utility";
import { usePropsStore } from "@/stores/props";
import type { DrawItemHistory } from "@/types/DrawItem";
import type { GridApi, GridReadyEvent, RowClassParams, RowHeightParams, ValueFormatterParams } from "ag-grid-community";

interface Props {
  drawItemHistory?: DrawItemHistory[];
  budgetColumns: number | null;
}
const props = defineProps<Props>();

const gridApi = ref<GridApi>();
const propsStore = usePropsStore();
const darkVal = computed<boolean>(() => propsStore.darkMode);

const rowClassRules = ref({
  "font-bold": (params: RowClassParams) => {
    return params.node.rowPinned != null;
  },
});

const defaultColDefs = {
  floatingFilter: false,
  sortable: false,
  enableCellChangeFlash: false,
  wrapHeaderText: true,
  autoHeaderHeight: true,
};

const getRowHeight = (params: RowHeightParams): number => {
  return params.node.rowPinned ? 25 : 32;
};

const resize = (): void => {
  gridApi.value?.autoSizeColumns([]);
};

const showColumns = (): void => {
  //Not sure if the draw item history should also be hiding columns based on the chosen template
  // const visible: string[] = [];
  // const invisible: string[] = [];
  // Object.entries(BudgetColumn).forEach((entry) => {
  //   if (props.budgetColumns == null || testBit(props.budgetColumns, entry[1].bit)) visible.push("item." + entry[0]);
  //   else invisible.push("item." + entry[0]);
  // });
  // gridApi.value?.setColumnsVisible(visible, true);
  // gridApi.value?.setColumnsVisible(invisible, false);
};

const colDefs = ref([
  {
    field: "drawNumber",
    headerName: "Draw",
    headerClass: "grid-align-center",
    pinned: true,
  },
  {
    field: "item.description",
    cellDataType: "text",
    headerName: "Description",
    headerClass: "grid-align-center",
    minWidth: 150,
    width: 400,
    pinned: true,
  },
  {
    field: "item.custCostType",
    cellDataType: "text",
    headerName: "Category",
    headerClass: "grid-align-center",
    cellEditor: "agSelectCellEditor",
    cellEditorParams: {
      values: [""],
    },
  },
  {
    field: "item.costType",
    cellDataType: "text",
    headerName: "Cost Type",
    headerClass: "grid-align-center",
    cellEditor: "agSelectCellEditor",
    cellEditorParams: {
      values: ["INSP", "NONINSP", "SUB"],
    },
  },
  {
    field: "item.budgetAmount",
    cellDataType: "number",
    headerName: "Budget Amount",
    valueFormatter: currencyFormatter,
  },
  {
    field: "budgetAdjustment",
    cellDataType: "number",
    headerName: "Budget Adjustment",
    valueFormatter: currencyFormatter,
  },
  {
    field: "item.adjustedBudget",
    cellDataType: "number",
    headerName: "Adjusted Budget",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, true),
  },
  {
    field: "item.priorDrawnToDate",
    cellDataType: "number",
    headerName: "Prior Drawn To Date",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, true),
  },
  {
    field: "item.hccPercent",
    cellDataType: "number",
    headerName: "HCC %",
    valueFormatter: (params: ValueFormatterParams) => percentFormatter(params, true),
  },
  {
    field: "item.priorEquityToDate",
    cellDataType: "number",
    headerName: "Prior Equity To Date",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, true),
  },
  {
    field: "item.totalThisDraw",
    cellDataType: "number",
    headerName: "This Draw Loan",
    valueFormatter: currencyFormatter,
  },
  {
    field: "item.totalThisEquity",
    cellDataType: "number",
    headerName: "This Draw Equity",
    valueFormatter: currencyFormatter,
  },
  {
    field: "item.netThisDraw",
    cellDataType: "number",
    headerName: "Net This Draw Loan",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, true),
  },
  {
    field: "item.drawnToDate",
    cellDataType: "number",
    headerName: "Loan Drawn To Date",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, true),
  },
  {
    field: "item.netThisDrawEquity",
    cellDataType: "number",
    headerName: "Net This Draw Equity",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, true),
  },
  {
    field: "item.equityToDate",
    cellDataType: "number",
    headerName: "Equity Drawn To Date",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, true),
  },
  {
    field: "item.totalDrawnToDate",
    cellDataType: "number",
    headerName: "Total Drawn To Date",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, true),
  },
  {
    field: "item.variance",
    cellDataType: "number",
    headerName: "Variance",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, true),
  },
  {
    field: "item.percentDrawn",
    cellDataType: "number",
    headerName: "% Draw",
    valueFormatter: (params: ValueFormatterParams) => percentFormatter(params, true),
  },
  {
    field: "item.percentInspectedPriorDraw",
    cellDataType: "number",
    headerName: "Insp Prior Draw",
    valueFormatter: (params: ValueFormatterParams) => percentFormatter(params, true),
  },
  {
    field: "item.percentInspectedThisDraw",
    cellDataType: "number",
    headerName: "This Draw Insp",
    valueFormatter: (params: ValueFormatterParams) => percentFormatter(params, params.data.custCostType == null || params.data.costType !== "INSP"),
  },
  {
    field: "item.percentInspected",
    cellDataType: "number",
    headerName: "Total Insp",
    valueFormatter: (params: ValueFormatterParams) => percentFormatter(params, true),
  },
  {
    field: "item.projectedOver",
    cellDataType: "number",
    headerName: "Projected Over / (Under)",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, true),
  },
  {
    field: "item.fundsRequired",
    cellDataType: "number",
    headerName: "Funds Required",
    valueFormatter: (params: ValueFormatterParams) => currencyFormatter(params, true),
  },
]);

onMounted(async (): Promise<void> => {
  showColumns();
});
</script>
